import { addBreadcrumb } from './breadcrumbs';
import { capturePerformanceMetric } from './errorReporter';

type CWVThreshold = [good: number, poor: number];

const THRESHOLDS: Record<string, CWVThreshold> = {
  LCP: [2500, 4000],
  INP: [200, 500],
  FID: [100, 300],
  FCP: [1800, 3000],
  TTFB: [800, 1800],
  CLS: [100, 250],
};

function rate(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name];
  if (!threshold) return 'good';
  if (value <= threshold[0]) return 'good';
  if (value <= threshold[1]) return 'needs-improvement';
  return 'poor';
}

function observe(
  type: string,
  callback: (entries: PerformanceEntryList) => void,
): PerformanceObserver | null {
  if (typeof PerformanceObserver === 'undefined') return null;
  try {
    const observer = new PerformanceObserver((list) => callback(list.getEntries()));
    observer.observe({ type, buffered: true });
    return observer;
  } catch {
    return null;
  }
}

export function initPerformanceMonitor(): void {
  if (typeof window === 'undefined') return;

  observe('largest-contentful-paint', (entries) => {
    const last = entries[entries.length - 1] as PerformanceEntry | undefined;
    if (!last) return;
    capturePerformanceMetric({
      name: 'LCP',
      value: last.startTime,
      rating: rate('LCP', last.startTime),
    });
  });

  observe('first-input', (entries) => {
    const first = entries[0] as (PerformanceEntry & { processingStart?: number }) | undefined;
    if (!first?.processingStart) return;
    const value = first.processingStart - first.startTime;
    if (value > 0) capturePerformanceMetric({ name: 'FID', value, rating: rate('FID', value) });
  });

  observe('event', (entries) => {
    for (const entry of entries) {
      const evt = entry as PerformanceEntry & { processingStart?: number; duration: number };
      const inp = evt.processingStart ? evt.processingStart - evt.startTime : evt.duration;
      if (inp > 0) {
        capturePerformanceMetric({ name: 'INP', value: inp, rating: rate('INP', inp) });
      }
    }
  });

  let clsValue = 0;
  observe('layout-shift', (entries) => {
    for (const entry of entries) {
      const shift = entry as PerformanceEntry & { value?: number; hadRecentInput?: boolean };
      if (!shift.hadRecentInput) clsValue += shift.value ?? 0;
    }
    const clsMs = Math.round(clsValue * 1000);
    capturePerformanceMetric({ name: 'CLS', value: clsMs, rating: rate('CLS', clsMs) });
  });

  observe('paint', (entries) => {
    for (const entry of entries) {
      if (entry.name === 'first-contentful-paint') {
        capturePerformanceMetric({
          name: 'FCP',
          value: entry.startTime,
          rating: rate('FCP', entry.startTime),
        });
      }
    }
  });

  observe('longtask', (entries) => {
    for (const entry of entries) {
      addBreadcrumb({
        type: 'system',
        message: 'performance:longtask',
        metadata: { durationMs: Math.round(entry.duration) },
      });
    }
  });

  const onLoad = () => {
    const navigation = performance.getEntriesByType('navigation')[0] as
      | (PerformanceEntry & { requestStart?: number; responseStart?: number })
      | undefined;
    if (!navigation?.requestStart || !navigation.responseStart) return;
    const ttfb = navigation.responseStart - navigation.requestStart;
    capturePerformanceMetric({ name: 'TTFB', value: ttfb, rating: rate('TTFB', ttfb) });
  };

  if (document.readyState === 'complete') onLoad();
  else window.addEventListener('load', onLoad, { once: true });
}
